<template>
  <div>
    <!-- Fondo oscuro del modal -->
    <div 
      v-if="isOpen" 
      class="modal-overlay" 
      @click="closeModal"
    ></div>

    <!-- Contenido del modal -->
    <div 
      v-if="isOpen" 
      class="modal-content"
    >
      <div class="modal-header">
        <h2>Asignar Usuarios a la Evaluación</h2>
        <button class="close-btn" @click="closeModal">&times;</button>
      </div>
      <form @submit.prevent="submitForm">
        <div class="modal-body">
          <!-- Select de usuarios con búsqueda -->
          <label for="users" class="block font-medium">Seleccione usuarios:</label>
          <v-select
            v-model="selectedUser"
            :options="filteredUsers"
            label="label"
            placeholder="Seleccione un usuario"
            @input="addUser"
          ></v-select>

          <!-- Lista de usuarios seleccionados -->
          <div v-if="selectedUsers.length > 0" class="mt-4">
            <h3 class="text-lg font-semibold">Usuarios seleccionados:</h3>
            <ul class="selected-users-list">
              <li 
                v-for="user in selectedUsers" 
                :key="user.id" 
                class="flex justify-between items-center"
              >
                {{ user.name }} {{ user.last_name }} - DEPTO: {{ user.specialty || 'Sin Departamento' }}
                <button 
                  type="button" 
                  class="remove-btn" 
                  :disabled="user.assigned_evaluations && user.assigned_evaluations[0].evaluation.completed_evaluations.length > 0"
                  @click="removeUser(user)"
                >
                  Quitar
                </button>
              </li>
            </ul>
          </div>
        </div>
        <div class="modal-footer">
          <button 
            type="submit" 
            :disabled="loading || selectedUsers.length === 0" 
            class="px-6 py-2 rounded-lg bg-indigo-600 text-white font-medium hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed btn-success"
          >
            {{ loading ? "Guardando..." : "Guardar" }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import request from '../../services/request';
import VSelect from 'vue-select';  // Importar vue-select
import 'vue-select/dist/vue-select.css';

export default {
  components: {
    VSelect,  // Registrar el componente
  },
  props: {
    evaluationId: Number,
    toUserId: Number,
    isOpen: Boolean,
  },
  data() {
    return {
      users: [], // Lista completa de usuarios obtenida del endpoint
      availableUsers: [], // Usuarios disponibles para seleccionar
      selectedUsers: [], // Usuarios seleccionados
      selectedUser: null, // Usuario actualmente seleccionado en el select
      searchQuery: "", // Texto de búsqueda para filtrar usuarios
      loading: false, // Estado de carga para la solicitud
    };
  },
  computed: {
    filteredUsers() {
    return this.availableUsers
      .filter((user) =>
        user.name.toLowerCase().includes(this.searchQuery.toLowerCase())
      )
      .map((user) => ({
        ...user,
        label: `${user.name.toUpperCase()} ${user.last_name.toUpperCase()} (Departamento : ${user.specialty || 'Sin Departamento'})`,
      }));
  }
  },
  methods: {
    async fetchUsers() {
      try {
        const { data } = await request.get("/evaluations/assigned-users");
        this.users = data.data;
        this.availableUsers = [...this.users]; // Inicializar usuarios disponibles
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    },

    async getAssignedUsers() {
      try {
        const { data } = await request.get("/evaluations/evaluation-users", {
          toUser: this.toUserId,
          evaluationId: this.evaluationId
        });
        this.selectedUsers = data.data;
        this.availableUsers = this.users.filter(
          (user) => !this.selectedUsers.some((selected) => selected.id === user.id)
        );
      } catch (error) {
        console.error("Error fetching assigned users:", error);
      }
    },

    addUser() {
      if (this.selectedUser) {
        this.selectedUsers.push(this.selectedUser);
        this.availableUsers = this.availableUsers.filter(
          (user) => user.id !== this.selectedUser.id
        );
        this.selectedUser = null; // Limpiar selección
      }
    },

    removeUser(user) {
      this.selectedUsers = this.selectedUsers.filter(
        (selected) => selected.id !== user.id
      );
      this.availableUsers.push(user);
    },

    async submitForm() {
      this.loading = true;
      try {
        const userIds = this.selectedUsers.map((user) => user.id);
        const { data } = await request.post(
          `/evaluations/${this.evaluationId}/assign-users`,
          { users: userIds, toUser: this.toUserId, evaluationId: this.evaluationId }
        );
        console.log("Users assigned successfully:", data);
        await this.getAssignedUsers(); // Llamar de nuevo para actualizar los usuarios asignados
        this.closeModal();
      } catch (error) {
        console.error("Error saving assigned users:", error);
      } finally {
        this.loading = false;
      }
    },

    closeModal() {
      this.$emit("save");
      this.$emit("close");
    },
  },
  watch: {
    isOpen(newVal) {
      if (newVal) {
        this.fetchUsers();
        this.getAssignedUsers();
        this.searchQuery = ""; // Limpiar búsqueda
      }
    },
  },
};
</script>

<style scoped>
/* Fondo oscuro del modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 999;
}

/* Contenedor del modal */
.modal-content {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 80%; /* Ancho aumentado */
  max-width: 900px; /* Mayor tamaño máximo */
  background-color: #fff;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  z-index: 1000;
  padding: 20px;
  max-height: 90vh; /* Altura máxima para evitar overflow */
  overflow-y: auto;
}

/* Encabezado del modal */
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #ddd;
  padding-bottom: 10px;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
}

/* Select y botones */
.selected-users-list {
  margin-top: 10px;
  list-style-type: none;
  padding: 0;
}

.selected-users-list li {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 5px 0;
}

.remove-btn {
  background-color: #e3342f;
  color: #fff;
  border: none;
  padding: 5px 10px;
  border-radius: 4px;
  cursor: pointer;
}

/* Pie del modal */
.modal-footer {
  display: flex;
  justify-content: flex-end;
  border-top: 1px solid #ddd;
  padding-top: 10px;
}

.save-btn {
  background-color: #007bff;
  color: #fff;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
}

.save-btn:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}
</style>
